import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { useTranslation } from '../i18n/useTranslation';
import { useTimeline } from '../hooks/useTimeline';
import { useAquariums } from '../hooks/useAquariums';
import { TimelineEventCard } from '../components/timeline/TimelineEventCard';
import { TimelineEmptyState } from '../components/timeline/TimelineEmptyState';
import { TimelineSkeleton } from '../components/timeline/TimelineSkeleton';
import { TimelineFilterChips } from '../components/timeline/TimelineFilterChips';
import type { TimelineFilterType } from '../types/timeline';
import { FILTER_TO_API_TYPES } from '../types/timeline';
import { Icon } from '../components/atoms/Icon';
import { isZeovitSupplySystem } from '../utils/supplySystem';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { LogTestsButton } from '../components/measurements/LogTestsButton';
import { useReturnFocus } from '../hooks/useReturnFocus';
import { Section } from '../components/templates/Section/Section';
import './AquariumTimelinePage.css';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';

interface NavLinkTile {
  key: string;
  labelKey: string;
  path: (id: string) => string;
  accent: string;
  icon: React.ReactNode;
  /** Only shown for aquariums whose supply system is ZeoVit (#1784). */
  requiresZeovitSupplySystem?: boolean;
}

const NAV_LINKS: readonly NavLinkTile[] = [
  {
    key: 'tasks',
    labelKey: 'nav.tasks',
    path: (id: string) => `/aquariums/${id}/tasks`,
    accent: 'var(--color-primary)',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <rect x="9" y="3" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    key: 'journal',
    labelKey: 'nav.journal',
    path: (id: string) => `/aquariums/${id}/journal`,
    accent: 'var(--color-status-success)',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="4" y="2" width="16" height="20" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    key: 'gallery',
    labelKey: 'nav.gallery',
    path: (id: string) => `/aquariums/${id}/gallery`,
    accent: 'var(--color-status-warning)',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="8" cy="10" r="2" fill="currentColor" opacity="0.7"/>
        <path d="M2 17l5-5 4 4 3-3 5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    key: 'livestock',
    labelKey: 'navItem.animals',
    path: (id: string) => `/aquariums/${id}/livestock`,
    accent: 'var(--color-secondary)',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 12c3-4.5 9-6 14-2l4-3v10l-4-3c-5 4-11 2.5-14-2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <circle cx="8" cy="11" r="1" fill="currentColor"/>
      </svg>
    ),
  },
  {
    key: 'icp',
    labelKey: 'nav.icpAnalyses',
    path: (id: string) => `/aquariums/${id}/icp-analyses`,
    accent: 'var(--color-status-info)',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 2v5M12 17v5M2 12h5M17 12h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
  },
  {
    key: 'waterTests',
    labelKey: 'aquarium.waterTests',
    path: (id: string) => `/aquariums/${id}/measurements`,
    accent: 'var(--color-status-ai)',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M9 2v10.5L6.5 18a4 4 0 0 0 11 0L15 12.5V2H9Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M9 2h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M7 16h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    // ZeoVit dosing module: the history page lists past recommendations and
    // offers "Start new recommendation" from its empty state. Only offered for
    // aquariums whose supply system is ZeoVit.
    key: 'zeovit',
    labelKey: 'navItem.zeovit',
    path: (id: string) => `/aquariums/${id}/zeovit-recommendations`,
    accent: 'var(--color-primary)',
    icon: <Icon name="flask" size={20} />,
    requiresZeovitSupplySystem: true,
  },
];

export function AquariumTimelinePage() {
  const { aquariumId = '' } = useParams<{ aquariumId: string }>();
  useReturnFocus();
  const { t, i18n } = useTranslation();
  const { data: aquariums } = useAquariums();
  const aquarium = aquariums?.find((a) => a.id === aquariumId);
  const usesZeovit = isZeovitSupplySystem(aquarium?.supplySystem);
  const navLinks = NAV_LINKS.filter((link) => !link.requiresZeovitSupplySystem || usesZeovit);
  const [activeFilter, setActiveFilter] = useState<TimelineFilterType>('all');
  const sentinelRef = useRef<HTMLDivElement>(null);

  const filterTypes = FILTER_TO_API_TYPES[activeFilter];
  const typesParam = filterTypes ? filterTypes.join(',') : undefined;

  const { data, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } = useTimeline(
    aquariumId,
    typesParam,
  );

  // Flatten pages into a single events array
  const events = data?.pages.flatMap((p) => p.data?.events ?? []) ?? [];

  // Intersection observer for infinite scroll
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const isEmpty = !isLoading && events.length === 0;

  // Group events by date string
  const grouped: { date: string; events: typeof events }[] = [];
  for (const event of events) {
    // Grouped by local calendar day, labelled in the app language.
    const dateStr = new Intl.DateTimeFormat(i18n.language, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(event.occurredAt));
    const last = grouped[grouped.length - 1];
    if (last && last.date === dateStr) {
      last.events.push(event);
    } else {
      grouped.push({ date: dateStr, events: [event] });
    }
  }

  // Under "Alle", a day's AI insights read as one card ("7 KI-Erkenntnisse") so photos,
  // tests and new animals are not buried; the AI filter still lists each (usability audit §5.7).
  const shownGroups =
    activeFilter !== 'all'
      ? grouped
      : grouped.map((group) => {
          const insights = group.events.filter((e) => e.type === 'ai_insight');
          if (insights.length < 2) return group;
          const first = insights[0]!;
          const collapsed = { ...first, id: `${first.id}-day`, title: t('timeline.aiInsightsCollapsed', { count: insights.length }) };
          return {
            ...group,
            events: group.events.filter((e) => e.type !== 'ai_insight' || e === first).map((e) => (e === first ? collapsed : e)),
          };
        });

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent>
          <PageHeader
            title={aquarium?.name ?? t('timeline.title')}
            actions={aquariumId ? <LogTestsButton aquariumId={aquariumId} id="timeline-log-tests" /> : undefined}
          />

          {/* Quick-nav cards — replaces the Overview page */}
          <nav className="timeline-page__nav" aria-label={t('aquarium.overview.quickLinks')}>
            {navLinks.map((link) => (
              <Link
                key={link.key}
                to={link.path(aquariumId)}
                className="timeline-page__nav-link"
                style={{ '--link-accent': link.accent } as React.CSSProperties}
              >
                <span className="timeline-page__nav-icon">{link.icon}</span>
                <span className="timeline-page__nav-label">{t(link.labelKey)}</span>
              </Link>
            ))}
          </nav>

          <Section title={t('timeline.title')}>
          <TimelineFilterChips activeFilter={activeFilter} onChange={setActiveFilter} />
          {isLoading && !data ? (
            <TimelineSkeleton />
          ) : isError ? (
            <LoadErrorState
              title={t('timeline.errorTitle')}
              titleLevel={3}
              message={t('timeline.errorBody')}
              onRetry={() => void refetch()}
            />
          ) : isEmpty ? (
            <TimelineEmptyState />
          ) : (
            <>
              {shownGroups.map((group) => (
                <div key={group.date} className="timeline-page__group">
                  <div className="timeline-page__date-label">{group.date}</div>
                  {group.events.map((event) => (
                    <TimelineEventCard key={event.id} event={event} aquariumId={aquariumId} />
                  ))}
                </div>
              ))}
              {isFetchingNextPage && (
                <div className="timeline-page__loading-more">{t('timeline.loadingMore')}</div>
              )}
              <div ref={sentinelRef} className="timeline-page__sentinel" aria-hidden="true" />
            </>
          )}
          </Section>
        </PageContent>
      }
    />
  );
}
